/**
 * Structured values of the bill condition facts `withholdingProfile`, `creditProfile` and
 * `allocationsWithinTargets` (ALC-4563, D39 to D41).
 *
 * One module serves the three places that must agree on what a value means: the fact that
 * publishes the state of a bill, the condition value a person or the MCP writes, and the
 * evaluator that compares the two. Everything here is pure and works on text, never on floats.
 *
 * The value of a profile is what was DECIDED, never an amount (D40): the tax and its rate in
 * percent. A rate is a canonical decimal string so two spellings of one rate ("1.5", "1.50", 1.5)
 * compare equal and two different rates never do. A profile always holds explicit rates (P42):
 * "the tax applies at the system rate" is not a value, so a rate the bill cannot resolve to a number
 * leaves the bill with no profile, and a condition that names a tax without a rate is refused.
 */

import { VENDOR_WITHHOLDING_TAXES, type VendorWithholdingTax } from './ap/vendor-knowledge.js';
import { PisCofinsCreditRevenueUse, PisCofinsCreditType } from './generated/enums.js';

// ---------------------------------------------------------------------------
// Rates
// ---------------------------------------------------------------------------

const RATE_TEXT_PATTERN = /^(\d{1,3})(?:\.(\d{1,4}))?$/;

/**
 * A rate in percent as canonical text: at least two decimals, at most four, trailing zeros beyond
 * the second dropped, greater than 0 and at most 100. `null` for anything else (a rate of 0 is a
 * tax that does not apply, which a profile expresses by leaving the tax out).
 *
 * A JSON number is read through its decimal text, never through arithmetic, so 4.65 is "4.65".
 */
export function canonicalRatePercent(input: unknown): string | null {
  let text: string;
  if (typeof input === 'number') {
    if (!Number.isFinite(input)) return null;
    text = String(input);
  } else if (typeof input === 'string') {
    text = input;
  } else {
    return null;
  }
  const match = RATE_TEXT_PATTERN.exec(text);
  if (!match) return null;
  const whole = Number(match[1]);
  let fraction = match[2] ?? '';
  while (fraction.length < 2) fraction += '0';
  while (fraction.length > 2 && fraction.endsWith('0')) fraction = fraction.slice(0, -1);
  const hasFraction = /[1-9]/.test(fraction);
  if (whole === 0 && !hasFraction) return null;
  if (whole > 100 || (whole === 100 && hasFraction)) return null;
  return `${whole}.${fraction}`;
}

// ---------------------------------------------------------------------------
// Withholding profile
// ---------------------------------------------------------------------------

/** The taxes a withholding profile can name, in the order a profile is always built. */
export const WITHHOLDING_PROFILE_TAXES = VENDOR_WITHHOLDING_TAXES;
export type WithholdingProfileTax = VendorWithholdingTax;

/**
 * The taxes a bill decided to withhold, each with its rate in percent as canonical text. A tax that
 * does not apply is not in the profile. `{}` is a decision too: nothing is withheld.
 */
export type WithholdingProfile = Partial<Record<WithholdingProfileTax, string>>;

function isPlainObject(input: unknown): input is Record<string, unknown> {
  return typeof input === 'object' && input !== null && !Array.isArray(input);
}

/**
 * The profile in canonical form, or `null` when the input is not a withholding profile. A tax
 * without an explicit rate (`null`, the old "system rate") is not a profile (P42).
 */
export function parseWithholdingProfile(input: unknown): WithholdingProfile | null {
  if (!isPlainObject(input)) return null;
  const known: readonly string[] = WITHHOLDING_PROFILE_TAXES;
  if (Object.keys(input).some((key) => !known.includes(key))) return null;
  const profile: WithholdingProfile = {};
  for (const tax of WITHHOLDING_PROFILE_TAXES) {
    if (!(tax in input)) continue;
    const rate = canonicalRatePercent(input[tax]);
    if (rate === null) return null;
    profile[tax] = rate;
  }
  return profile;
}

/**
 * Whether the input names a tax of the profile without a rate (`null` or an empty rate): the one
 * way a profile value is refused with its own code (P42), because the fix is to type the rate.
 */
export function withholdingProfileLacksRate(input: unknown): boolean {
  if (!isPlainObject(input)) return false;
  const known: readonly string[] = WITHHOLDING_PROFILE_TAXES;
  return Object.entries(input).some(
    ([tax, rate]) => known.includes(tax) && (rate === null || rate === ''),
  );
}

/**
 * The "same profile as" test for withholdings: the same taxes at the same rates. A side that is not
 * a profile (no decision recorded) is never the same as anything, itself included.
 */
export function sameWithholdingProfile(left: unknown, right: unknown): boolean {
  const a = parseWithholdingProfile(left);
  const b = parseWithholdingProfile(right);
  if (a === null || b === null) return false;
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  return keysA.every(
    (tax, index) =>
      keysB[index] === tax && a[tax as WithholdingProfileTax] === b[tax as WithholdingProfileTax],
  );
}

// ---------------------------------------------------------------------------
// Credit profile
// ---------------------------------------------------------------------------

/**
 * The PIS/COFINS credit a bill decided on, in the vocabulary of the bill columns. PIS and COFINS
 * stay apart. `null` is "not decided" for that field. No amount is part of a profile.
 */
// A type alias, not an interface: a fact value must be assignable to the snapshot's JSON shape,
// and only an alias has the implicit index signature that needs.
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type CreditProfile = {
  generatesPisCredit: boolean | null;
  generatesCofinsCredit: boolean | null;
  creditType: PisCofinsCreditType | null;
  revenueUse: PisCofinsCreditRevenueUse | null;
};

const CREDIT_PROFILE_KEYS: readonly string[] = [
  'generatesPisCredit',
  'generatesCofinsCredit',
  'creditType',
  'revenueUse',
];

function readOptionalBoolean(value: unknown): boolean | null | undefined {
  if (value === undefined || value === null) return null;
  return typeof value === 'boolean' ? value : undefined;
}

function readOptionalMember<T extends string>(
  value: unknown,
  members: Record<string, T>,
): T | null | undefined {
  if (value === undefined || value === null) return null;
  const allowed: readonly unknown[] = Object.values(members);
  return typeof value === 'string' && allowed.includes(value) ? (value as T) : undefined;
}

/**
 * The profile in canonical form, or `null` when the input is not a credit profile or decides
 * nothing. When neither tax generates credit, the type and the revenue use are dropped: they say
 * nothing about a bill that takes no credit.
 */
export function parseCreditProfile(input: unknown): CreditProfile | null {
  if (!isPlainObject(input)) return null;
  if (Object.keys(input).some((key) => !CREDIT_PROFILE_KEYS.includes(key))) return null;
  const generatesPisCredit = readOptionalBoolean(input.generatesPisCredit);
  const generatesCofinsCredit = readOptionalBoolean(input.generatesCofinsCredit);
  let creditType = readOptionalMember(input.creditType, PisCofinsCreditType);
  let revenueUse = readOptionalMember(input.revenueUse, PisCofinsCreditRevenueUse);
  if (
    generatesPisCredit === undefined ||
    generatesCofinsCredit === undefined ||
    creditType === undefined ||
    revenueUse === undefined
  ) {
    return null;
  }
  if (generatesPisCredit === false && generatesCofinsCredit === false) {
    creditType = null;
    revenueUse = null;
  }
  if (
    generatesPisCredit === null &&
    generatesCofinsCredit === null &&
    creditType === null &&
    revenueUse === null
  ) {
    return null;
  }
  return { generatesPisCredit, generatesCofinsCredit, creditType, revenueUse };
}

/** The "same profile as" test for credits. A side that decides nothing is never the same. */
export function sameCreditProfile(left: unknown, right: unknown): boolean {
  const a = parseCreditProfile(left);
  const b = parseCreditProfile(right);
  if (a === null || b === null) return false;
  return (
    a.generatesPisCredit === b.generatesPisCredit &&
    a.generatesCofinsCredit === b.generatesCofinsCredit &&
    a.creditType === b.creditType &&
    a.revenueUse === b.revenueUse
  );
}

/**
 * The "same profile as" test when the caller does not know which profile it holds (the evaluator
 * reads a state and a condition value, not a fact definition). A withholding profile and a credit
 * profile have no key in common, so a pair is compared as the one both sides are, and a pair of
 * different kinds is never the same.
 */
export function sameProfile(left: unknown, right: unknown): boolean {
  return sameWithholdingProfile(left, right) || sameCreditProfile(left, right);
}

// ---------------------------------------------------------------------------
// Allocation targets
// ---------------------------------------------------------------------------

/** The allocation dimensions with a flat list of targets, in the order a value is always built. */
export const ALLOCATION_TARGET_DIMENSIONS = [
  'glAccounts',
  'costCenters',
  'departments',
  'managerialAccounts',
] as const;
export type AllocationTargetDimension = (typeof ALLOCATION_TARGET_DIMENSIONS)[number];

/**
 * The sets a condition names. A dimension that is absent is not tested. A dimension with a
 * non-empty list holds only when the bill has a target in it and EVERY row and line of the bill
 * has one in the list (P40: a bill that is not classified, or only partly, is not within). A
 * dimension with `[]` means the bill must have no target in it. Segments are keyed by segment id.
 */
export type AllocationTargets = Partial<Record<AllocationTargetDimension, string[]>> & {
  segments?: Record<string, string[]>;
};

/**
 * Every target of a bill, over all its classification rows, bill and line level, and where the
 * allocation is incomplete (P40). A dimension (or segment) is PARTIAL when the bill has a target in
 * it but some row of a split has no id, some row of a custom breakdown has none, or some line of a
 * line-level bill has no classification: those rows are allocated nowhere.
 */
export type AllocationTargetsState = Record<AllocationTargetDimension, string[]> & {
  segments: Record<string, string[]>;
  partialDimensions: AllocationTargetDimension[];
  partialSegments: string[];
  /**
   * ALC-4658 (D50, P50, P51): the stored percent of each target of a bill-level SIMPLE split, what
   * `exact_split` compares. `null` when the bill is not such a split (line level, a custom
   * breakdown, more than one bill-level row, no classification): `exact_split` is then false.
   * Absent in a state stored before ALC-4658, which reads as `null`.
   */
  split?: AllocationSplitState | null;
};

/**
 * One target of a bill-level simple split and its stored percent as canonical text with exactly two
 * decimals ("60.00"). `percent` is `null` when the stored percent is missing, unreadable, or has a
 * non-zero digit past the second decimal: that target never equals a rule percent (fails closed).
 * A target listed twice in one dimension is one share holding the sum.
 */
// A type alias, not an interface: a fact value must be assignable to the snapshot's JSON shape,
// and only an alias has the implicit index signature that needs.
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type AllocationSplitShare = {
  id: string;
  percent: string | null;
};

/** The shares per dimension (and per segment, by segment id), each list sorted by id. */
export type AllocationSplitState = Record<AllocationTargetDimension, AllocationSplitShare[]> & {
  segments: Record<string, AllocationSplitShare[]>;
};

const ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function readIdSet(input: unknown): string[] | null {
  if (!Array.isArray(input)) return null;
  const ids = new Set<string>();
  for (const entry of input) {
    if (typeof entry !== 'string' || !ID_PATTERN.test(entry)) return null;
    ids.add(entry.toLowerCase());
  }
  return [...ids].sort();
}

function readSegmentSets(input: unknown): Record<string, string[]> | null {
  if (!isPlainObject(input)) return null;
  const out: Record<string, string[]> = {};
  const segmentIds: string[] = [];
  for (const key of Object.keys(input)) {
    if (!ID_PATTERN.test(key)) return null;
    segmentIds.push(key.toLowerCase());
  }
  segmentIds.sort();
  for (const key of Object.keys(input)) {
    const values = readIdSet(input[key]);
    if (values === null) return null;
    out[key.toLowerCase()] = values;
  }
  const sorted: Record<string, string[]> = {};
  for (const id of segmentIds) sorted[id] = out[id] ?? [];
  return sorted;
}

/**
 * The condition value in canonical form (lists sorted and de-duplicated, ids lower case), or
 * `null` when it is not an allocation targets value or tests nothing.
 */
export function parseAllocationTargetsCondition(input: unknown): AllocationTargets | null {
  if (!isPlainObject(input)) return null;
  const known: readonly string[] = [...ALLOCATION_TARGET_DIMENSIONS, 'segments'];
  if (Object.keys(input).some((key) => !known.includes(key))) return null;
  const targets: AllocationTargets = {};
  let tests = false;
  for (const dimension of ALLOCATION_TARGET_DIMENSIONS) {
    if (!(dimension in input)) continue;
    const ids = readIdSet(input[dimension]);
    if (ids === null) return null;
    targets[dimension] = ids;
    tests = true;
  }
  if ('segments' in input) {
    const segments = readSegmentSets(input.segments);
    if (segments === null) return null;
    if (Object.keys(segments).length > 0) {
      targets.segments = segments;
      tests = true;
    }
  }
  return tests ? targets : null;
}

/** The condition field this value belongs to. */
export const ALLOCATIONS_WITHIN_TARGETS_FIELD = 'allocations_within_targets';

/**
 * An allocation target a condition value names, as the entity-bound reference it is (ALC-4563,
 * P41). The flat dimensions map to the reference types the `gl_account`, `cost_center`,
 * `department` and `managerial_account` condition fields use; a segment names the segment itself
 * and each of its values (`segment_value`, as the `segment[<id>]` field does).
 */
export type AllocationTargetReference =
  | { type: 'gl_account' | 'cost_center' | 'department' | 'managerial_account'; recordId: string }
  | { type: 'segment'; recordId: string }
  | { type: 'segment_value'; recordId: string; segmentId: string };

const ALLOCATION_DIMENSION_REFERENCE_TYPE = {
  glAccounts: 'gl_account',
  costCenters: 'cost_center',
  departments: 'department',
  managerialAccounts: 'managerial_account',
} as const satisfies Record<AllocationTargetDimension, string>;

/**
 * Every record an allocation targets condition value names, in canonical order, so the workflow
 * entity policy can check it against the workflow's entity and the dependency registry can list
 * the workflows that name a cost center, a GL account, a department, a managerial account or a
 * segment value. A value that is not a valid allocation targets value names nothing (the condition
 * validator refuses it; this never throws).
 */
export function allocationTargetReferences(value: unknown): AllocationTargetReference[] {
  // ALC-4658: an `exact_split` value names its targets inside `{ id, percent }` entries. Reading only
  // the id lists would let a rule name another entity's cost center unseen (silent), so both shapes
  // are read here, and the percents are ignored.
  const targets = allocationConditionTargetIds(value);
  if (targets === null) return [];
  const references: AllocationTargetReference[] = [];
  for (const dimension of ALLOCATION_TARGET_DIMENSIONS) {
    for (const recordId of targets[dimension] ?? []) {
      references.push({ type: ALLOCATION_DIMENSION_REFERENCE_TYPE[dimension], recordId });
    }
  }
  for (const [segmentId, valueIds] of Object.entries(targets.segments ?? {})) {
    references.push({ type: 'segment', recordId: segmentId });
    for (const recordId of valueIds) {
      references.push({ type: 'segment_value', recordId, segmentId });
    }
  }
  return references;
}

/**
 * The state is read leniently: it is what the bill carries, and a bill may carry a value no
 * condition can name (a free-text segment value, a legacy id). Such a value is simply outside
 * every set a condition gives. Ids that look like uuids are lower-cased so they meet the condition.
 */
function readStateIds(input: unknown): string[] | null {
  if (!Array.isArray(input)) return null;
  const ids = new Set<string>();
  for (const entry of input) {
    if (typeof entry !== 'string' || entry.length === 0) return null;
    ids.add(ID_PATTERN.test(entry) ? entry.toLowerCase() : entry);
  }
  return [...ids].sort();
}

function readStateDimensions(input: unknown): AllocationTargetDimension[] | null {
  if (!Array.isArray(input)) return null;
  const known: readonly unknown[] = ALLOCATION_TARGET_DIMENSIONS;
  if (input.some((entry) => !known.includes(entry))) return null;
  return ALLOCATION_TARGET_DIMENSIONS.filter((dimension) => input.includes(dimension));
}

function parseAllocationTargetsState(input: unknown): AllocationTargetsState | null {
  if (!isPlainObject(input)) return null;
  const state: Partial<AllocationTargetsState> = {};
  for (const dimension of ALLOCATION_TARGET_DIMENSIONS) {
    const ids = readStateIds(input[dimension]);
    if (ids === null) return null;
    state[dimension] = ids;
  }
  if (!isPlainObject(input.segments)) return null;
  const segments: Record<string, string[]> = {};
  for (const segmentId of Object.keys(input.segments).sort()) {
    const values = readStateIds(input.segments[segmentId]);
    if (values === null) return null;
    segments[ID_PATTERN.test(segmentId) ? segmentId.toLowerCase() : segmentId] = values;
  }
  state.segments = segments;
  // A state that does not say where it is partial (one stored before P40) cannot prove a bill is
  // fully allocated, so it never holds.
  const partialDimensions = readStateDimensions(input.partialDimensions);
  const partialSegments = readStateIds(input.partialSegments);
  if (partialDimensions === null || partialSegments === null) return null;
  state.partialDimensions = partialDimensions;
  state.partialSegments = partialSegments;
  // ALC-4658: read leniently. A state stored before the split existed, or one whose split is not
  // readable, is still a valid state for the set operators; only `exact_split` needs the split.
  state.split = parseAllocationSplitState(input.split);
  return state as AllocationTargetsState;
}

function readSplitShares(input: unknown): AllocationSplitShare[] | null {
  if (!Array.isArray(input)) return null;
  const shares: AllocationSplitShare[] = [];
  const seen = new Set<string>();
  for (const entry of input) {
    if (!isPlainObject(entry) || typeof entry.id !== 'string' || entry.id.length === 0) return null;
    const id = ID_PATTERN.test(entry.id) ? entry.id.toLowerCase() : entry.id;
    if (seen.has(id)) return null;
    seen.add(id);
    const percent =
      typeof entry.percent === 'string' && SPLIT_STATE_PERCENT_PATTERN.test(entry.percent)
        ? entry.percent
        : null;
    shares.push({ id, percent });
  }
  return shares.sort((left, right) => compareText(left.id, right.id));
}

function parseAllocationSplitState(input: unknown): AllocationSplitState | null {
  if (!isPlainObject(input) || !isPlainObject(input.segments)) return null;
  const split: Partial<AllocationSplitState> = {};
  for (const dimension of ALLOCATION_TARGET_DIMENSIONS) {
    const shares = readSplitShares(input[dimension]);
    if (shares === null) return null;
    split[dimension] = shares;
  }
  const segments: Record<string, AllocationSplitShare[]> = {};
  for (const segmentId of Object.keys(input.segments).sort()) {
    const shares = readSplitShares(input.segments[segmentId]);
    if (shares === null) return null;
    segments[ID_PATTERN.test(segmentId) ? segmentId.toLowerCase() : segmentId] = shares;
  }
  split.segments = segments;
  return split as AllocationSplitState;
}

/** A stored share percent as the fact writes it: two decimals exactly, never an exponent. */
const SPLIT_STATE_PERCENT_PATTERN = /^-?\d+\.\d{2}$/;

function compareText(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0;
}

function allWithin(ids: readonly string[], allowed: readonly string[]): boolean {
  const set = new Set(allowed);
  return ids.every((id) => set.has(id));
}

/**
 * One dimension (or segment) of the test. `[]` asks for no target at all. A list asks for at least
 * one target, all of them in the list, and no row or line left without a target (P40): an
 * unclassified bill, or one classified in part, is never within a list, because the part with no
 * target went somewhere nobody approved.
 */
function holdsWithin(
  used: readonly string[],
  allowed: readonly string[],
  partial: boolean,
): boolean {
  if (allowed.length === 0) return used.length === 0;
  return used.length > 0 && !partial && allWithin(used, allowed);
}

/**
 * Whether the allocation of the bill is inside the sets the condition names. Only the dimensions
 * the condition names are tested (see {@link AllocationTargets}). A state or a condition that is
 * not a valid value never holds.
 */
export function isAllocationsWithinTargets(state: unknown, condition: unknown): boolean {
  const targets = parseAllocationTargetsCondition(condition);
  const bill = parseAllocationTargetsState(state);
  if (targets === null || bill === null) return false;
  for (const dimension of ALLOCATION_TARGET_DIMENSIONS) {
    const allowed = targets[dimension];
    if (
      allowed !== undefined &&
      !holdsWithin(bill[dimension], allowed, bill.partialDimensions.includes(dimension))
    ) {
      return false;
    }
  }
  if (targets.segments !== undefined) {
    for (const [segmentId, allowed] of Object.entries(targets.segments)) {
      if (
        !holdsWithin(
          bill.segments[segmentId] ?? [],
          allowed,
          bill.partialSegments.includes(segmentId),
        )
      ) {
        return false;
      }
    }
  }
  return true;
}

// ---------------------------------------------------------------------------
// Split modes (ALC-4658, D50, P50 to P55)
// ---------------------------------------------------------------------------

/**
 * The operators of the `allocations_within_targets` fact, spelled here so this module does not
 * import `wf.ts` (`BillWorkflowConditionOperator` holds the same strings):
 *
 * - `same_targets` "Rateio entre estes": per dimension named, the set of targets the bill uses EQUALS
 *   the list (each listed target used, no other target), any percent, no partial row (P40).
 * - `exact_split` "Exatamente estes %": only a bill-level SIMPLE split; the set equals the list AND
 *   each target's stored percent equals the rule percent (P50). Fails closed (false) on a line-level
 *   bill, a custom breakdown, or a state with no split (P51).
 * - `any_within` "Pelo menos um destes": at least one target of the bill is in the list; other
 *   targets and partial rows are allowed. No target at all fails.
 * - `all_within` (the subset test of ALC-4563): kept for the rules that exist (P55), no longer
 *   offered for a new condition.
 */
export const ALLOCATION_TARGETS_OPERATORS = [
  'same_targets',
  'exact_split',
  'any_within',
  'all_within',
] as const;
export type AllocationTargetsOperator = (typeof ALLOCATION_TARGETS_OPERATORS)[number];

export function isAllocationTargetsOperator(
  operator: unknown,
): operator is AllocationTargetsOperator {
  return (
    typeof operator === 'string' &&
    (ALLOCATION_TARGETS_OPERATORS as readonly string[]).includes(operator)
  );
}

/**
 * P52: whether the mode bounds the set of targets the bill may use, so an approval authority
 * restricted to some cost centers / departments can cover the rule. `any_within` lets the bill use
 * any other target too, so it bounds nothing (the D37 coverage check must treat it as "no cost
 * center / department condition"). Per operator only: ALC-4565's `deriveAutoApproveScope` must read
 * the per-dimension {@link allocationConditionBoundedTargets}, which is built on this.
 */
export function allocationModeBoundsTargets(operator: unknown): boolean {
  return operator === 'same_targets' || operator === 'exact_split' || operator === 'all_within';
}

/** Per dimension, and for segments as a whole: whether a condition bounds its targets there. */
export type AllocationBoundedTargets = Record<AllocationTargetDimension, boolean> & {
  segments: boolean;
};

/**
 * Per dimension (and for segments as a whole), whether a condition bounds the targets the bill may
 * use there. A dimension is bounded only when the mode bounds ({@link allocationModeBoundsTargets})
 * AND the value names that dimension with at least one target. Examples:
 * - `same_targets { glAccounts: [3.1.05] }` bounds GL accounts only: `costCenters` is false.
 * - `any_within { costCenters: [CC-001] }` bounds nothing: every key is false.
 * - `exact_split { costCenters: [CC-001 60%, CC-002 40%] }` bounds cost centers: `costCenters` true.
 * - `all_within { costCenters: [] }` ("no cost center") names no target: `costCenters` is false.
 * A value the operator refuses (wrong shape, a split that does not sum to 100.00) or an operator
 * that is not a split mode bounds nothing.
 *
 * ALC-4565: the auto-approve coverage check (`deriveAutoApproveScope`) MUST read this, per
 * dimension. The per-operator {@link allocationModeBoundsTargets} alone is not enough: a GL-only or
 * segment-only `same_targets` rule says nothing about cost centers or departments.
 */
export function allocationConditionBoundedTargets(
  operator: unknown,
  value: unknown,
): AllocationBoundedTargets {
  const bounded = { segments: false } as AllocationBoundedTargets;
  for (const dimension of ALLOCATION_TARGET_DIMENSIONS) bounded[dimension] = false;
  if (!allocationModeBoundsTargets(operator)) return bounded;
  if (allocationTargetsConditionProblem(operator, value) !== null) return bounded;
  const ids = allocationConditionTargetIds(value);
  if (ids === null) return bounded;
  for (const dimension of ALLOCATION_TARGET_DIMENSIONS) {
    bounded[dimension] = (ids[dimension]?.length ?? 0) > 0;
  }
  bounded.segments = Object.values(ids.segments ?? {}).some((list) => list.length > 0);
  return bounded;
}

/**
 * One target of an `exact_split` value and its percent. `percent` is text with at most two
 * decimals ("60", "60.0", "60.00" and the JSON number 60 are one percent); it is canonicalised to
 * two decimals ("60.00") when parsed.
 */
// A type alias, not an interface: a fact value must be assignable to the snapshot's JSON shape,
// and only an alias has the implicit index signature that needs.
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type AllocationSplitEntry = {
  id: string;
  percent: string;
};

/**
 * The value of an `exact_split` condition: per dimension named, the targets and their percents,
 * which sum to exactly 100.00 per dimension. Segments are keyed by segment id. Example (60/40):
 * `{ costCenters: [{ id: <CC-001>, percent: "60.00" }, { id: <CC-002>, percent: "40.00" }] }`.
 */
export type AllocationSplit = Partial<Record<AllocationTargetDimension, AllocationSplitEntry[]>> & {
  segments?: Record<string, AllocationSplitEntry[]>;
};

const SPLIT_PERCENT_PATTERN = /^(\d{1,3})(?:\.(\d{1,2}))?$/;
const FULL_SPLIT_BASIS_POINTS = 10_000;

/**
 * A split percent in basis points (60.00 -> 6000), read from its decimal text and never through
 * float arithmetic. `null` unless it is more than 0 and at most 100 with at most two decimals.
 */
export function splitPercentBasisPoints(input: unknown): number | null {
  let text: string;
  if (typeof input === 'number') {
    if (!Number.isFinite(input)) return null;
    text = String(input);
  } else if (typeof input === 'string') {
    text = input;
  } else {
    return null;
  }
  const match = SPLIT_PERCENT_PATTERN.exec(text);
  if (!match) return null;
  const fraction = (match[2] ?? '').padEnd(2, '0');
  const basisPoints = Number(match[1]) * 100 + Number(fraction);
  if (basisPoints <= 0 || basisPoints > FULL_SPLIT_BASIS_POINTS) return null;
  return basisPoints;
}

function basisPointsText(basisPoints: number): string {
  const whole = Math.floor(basisPoints / 100);
  const fraction = String(basisPoints % 100).padStart(2, '0');
  return `${whole}.${fraction}`;
}

/** Why an `exact_split` value is refused: not a value at all, or percents not summing to 100.00. */
export type AllocationSplitProblem = 'VALUE_INVALID' | 'SPLIT_SUM_NOT_100';

type SplitEntriesRead = { ok: true; entries: AllocationSplitEntry[]; sum: number } | { ok: false };

function readSplitEntries(input: unknown): SplitEntriesRead {
  if (!Array.isArray(input) || input.length === 0) return { ok: false };
  const seen = new Set<string>();
  const entries: AllocationSplitEntry[] = [];
  let sum = 0;
  for (const entry of input) {
    if (!isPlainObject(entry)) return { ok: false };
    if (Object.keys(entry).some((key) => key !== 'id' && key !== 'percent')) return { ok: false };
    if (typeof entry.id !== 'string' || !ID_PATTERN.test(entry.id)) return { ok: false };
    const id = entry.id.toLowerCase();
    if (seen.has(id)) return { ok: false };
    seen.add(id);
    const basisPoints = splitPercentBasisPoints(entry.percent);
    if (basisPoints === null) return { ok: false };
    sum += basisPoints;
    entries.push({ id, percent: basisPointsText(basisPoints) });
  }
  entries.sort((left, right) => compareText(left.id, right.id));
  return { ok: true, entries, sum };
}

function readSplitCondition(input: unknown): { split: AllocationSplit; sumsTo100: boolean } | null {
  if (!isPlainObject(input)) return null;
  const known: readonly string[] = [...ALLOCATION_TARGET_DIMENSIONS, 'segments'];
  if (Object.keys(input).some((key) => !known.includes(key))) return null;
  const split: AllocationSplit = {};
  let sumsTo100 = true;
  let tests = false;
  for (const dimension of ALLOCATION_TARGET_DIMENSIONS) {
    if (!(dimension in input)) continue;
    const read = readSplitEntries(input[dimension]);
    if (!read.ok) return null;
    split[dimension] = read.entries;
    sumsTo100 &&= read.sum === FULL_SPLIT_BASIS_POINTS;
    tests = true;
  }
  if ('segments' in input) {
    if (!isPlainObject(input.segments)) return null;
    const segments: Record<string, AllocationSplitEntry[]> = {};
    for (const key of Object.keys(input.segments).sort()) {
      if (!ID_PATTERN.test(key)) return null;
      const read = readSplitEntries(input.segments[key]);
      if (!read.ok) return null;
      segments[key.toLowerCase()] = read.entries;
      sumsTo100 &&= read.sum === FULL_SPLIT_BASIS_POINTS;
    }
    if (Object.keys(segments).length > 0) {
      split.segments = segments;
      tests = true;
    }
  }
  return tests ? { split, sumsTo100 } : null;
}

/**
 * The `exact_split` value in canonical form (entries sorted by id, ids lower case, percents with
 * two decimals), or `null` when it is not one: not the `{ id, percent }` shape, a duplicate id, a
 * percent of 0 or less, above 100 or with more than two decimals, or percents of one dimension that
 * do not sum to exactly 100.00.
 */
export function parseAllocationSplitCondition(input: unknown): AllocationSplit | null {
  const read = readSplitCondition(input);
  return read !== null && read.sumsTo100 ? read.split : null;
}

/** Why an `exact_split` value is refused, or `null` when it is a valid value. */
export function allocationSplitConditionProblem(input: unknown): AllocationSplitProblem | null {
  const read = readSplitCondition(input);
  if (read === null) return 'VALUE_INVALID';
  return read.sumsTo100 ? null : 'SPLIT_SUM_NOT_100';
}

/** True when every list the value names (dimension or segment) has at least one id. */
function namesNoEmptyList(targets: AllocationTargets): boolean {
  for (const dimension of ALLOCATION_TARGET_DIMENSIONS) {
    const ids = targets[dimension];
    if (ids !== undefined && ids.length === 0) return false;
  }
  return Object.values(targets.segments ?? {}).every((ids) => ids.length > 0);
}

/**
 * Why the value does not fit the operator of an `allocations_within_targets` condition, or `null`.
 * - `all_within` keeps the id lists of ALC-4563, `[]` included ("no target here").
 * - `same_targets` and `any_within` take the same id lists, but never `[]`: "the set equals
 *   nothing" is refused by the spec (an empty bill set fails) and "at least one of nothing" can
 *   never hold.
 * - `exact_split` takes `{ id, percent }` entries summing to 100.00 per dimension.
 * The id-list shape under `exact_split`, or the entry shape under another operator, is refused:
 * stored, it would match nothing, silently.
 */
export function allocationTargetsConditionProblem(
  operator: unknown,
  value: unknown,
): AllocationSplitProblem | null {
  if (operator === 'exact_split') return allocationSplitConditionProblem(value);
  const targets = parseAllocationTargetsCondition(value);
  if (targets === null) return 'VALUE_INVALID';
  if (operator === 'all_within') return null;
  if (operator === 'same_targets' || operator === 'any_within') {
    return namesNoEmptyList(targets) ? null : 'VALUE_INVALID';
  }
  return 'VALUE_INVALID';
}

/**
 * The id sets a value names, whichever shape it has (the id lists, or the `exact_split` entries,
 * whose percents are ignored here), or `null` when it is neither. For the readers that need to know
 * WHICH records a condition names (entity policy, dependency registry, segment catalog check) and
 * must not miss one because of the shape. The entry shape is read leniently (a wrong sum still
 * names its ids) so a refused value is never the reason a reference goes unseen.
 */
export function allocationConditionTargetIds(value: unknown): AllocationTargets | null {
  const targets = parseAllocationTargetsCondition(value);
  if (targets !== null) return targets;
  if (!isPlainObject(value)) return null;
  const known: readonly string[] = [...ALLOCATION_TARGET_DIMENSIONS, 'segments'];
  if (Object.keys(value).some((key) => !known.includes(key))) return null;
  const ids: AllocationTargets = {};
  let names = false;
  for (const dimension of ALLOCATION_TARGET_DIMENSIONS) {
    if (!(dimension in value)) continue;
    const read = readEntryIds(value[dimension]);
    if (read === null) return null;
    ids[dimension] = read;
    names = true;
  }
  if ('segments' in value) {
    if (!isPlainObject(value.segments)) return null;
    const segments: Record<string, string[]> = {};
    for (const key of Object.keys(value.segments).sort()) {
      if (!ID_PATTERN.test(key)) return null;
      const read = readEntryIds(value.segments[key]);
      if (read === null) return null;
      segments[key.toLowerCase()] = read;
    }
    if (Object.keys(segments).length > 0) {
      ids.segments = segments;
      names = true;
    }
  }
  return names ? ids : null;
}

/** The ids of `{ id, ... }` entries, whatever their percents; `null` when an entry has no id. */
function readEntryIds(input: unknown): string[] | null {
  if (!Array.isArray(input)) return null;
  const ids = new Set<string>();
  for (const entry of input) {
    if (!isPlainObject(entry) || typeof entry.id !== 'string' || !ID_PATTERN.test(entry.id)) {
      return null;
    }
    ids.add(entry.id.toLowerCase());
  }
  return [...ids].sort(compareText);
}

function sameSet(used: readonly string[], listed: readonly string[]): boolean {
  if (used.length !== listed.length) return false;
  const set = new Set(listed);
  return used.every((id) => set.has(id));
}

/** `same_targets` on one dimension: a whole allocation onto exactly the listed targets. */
function holdsSameTargets(
  used: readonly string[],
  listed: readonly string[],
  partial: boolean,
): boolean {
  return listed.length > 0 && used.length > 0 && !partial && sameSet(used, listed);
}

/** `any_within` on one dimension: at least one target of the bill is listed. */
function holdsAnyWithin(used: readonly string[], listed: readonly string[]): boolean {
  const set = new Set(listed);
  return used.some((id) => set.has(id));
}

/** `exact_split` on one dimension: the stored shares are exactly the listed entries. */
function holdsExactSplit(
  shares: readonly AllocationSplitShare[] | undefined,
  entries: readonly AllocationSplitEntry[],
  partial: boolean,
): boolean {
  if (partial || shares === undefined || shares.length !== entries.length) return false;
  const byId = new Map(shares.map((share) => [share.id, share.percent]));
  return entries.every((entry) => {
    const stored = byId.get(entry.id);
    if (stored === undefined || stored === null) return false;
    // Both sides are two-decimal text; compare as integer basis points, never as floats.
    return splitPercentBasisPoints(stored) === splitPercentBasisPoints(entry.percent);
  });
}

function everyNamedDimension(
  targets: AllocationTargets,
  bill: AllocationTargetsState,
  holds: (used: readonly string[], listed: readonly string[], partial: boolean) => boolean,
): boolean {
  for (const dimension of ALLOCATION_TARGET_DIMENSIONS) {
    const listed = targets[dimension];
    if (
      listed !== undefined &&
      !holds(bill[dimension], listed, bill.partialDimensions.includes(dimension))
    ) {
      return false;
    }
  }
  for (const [segmentId, listed] of Object.entries(targets.segments ?? {})) {
    if (!holds(bill.segments[segmentId] ?? [], listed, bill.partialSegments.includes(segmentId))) {
      return false;
    }
  }
  return true;
}

/** `same_targets`: see {@link ALLOCATION_TARGETS_OPERATORS}. */
export function isAllocationsSameTargets(state: unknown, condition: unknown): boolean {
  const targets = parseAllocationTargetsCondition(condition);
  const bill = parseAllocationTargetsState(state);
  if (targets === null || bill === null) return false;
  return everyNamedDimension(targets, bill, holdsSameTargets);
}

/** `any_within`: see {@link ALLOCATION_TARGETS_OPERATORS}. Partial rows are allowed. */
export function isAllocationsAnyWithin(state: unknown, condition: unknown): boolean {
  const targets = parseAllocationTargetsCondition(condition);
  const bill = parseAllocationTargetsState(state);
  if (targets === null || bill === null) return false;
  return everyNamedDimension(targets, bill, (used, listed) => holdsAnyWithin(used, listed));
}

/**
 * `exact_split`: see {@link ALLOCATION_TARGETS_OPERATORS}. False when the bill is not a bill-level
 * SIMPLE split (`split` null or absent), when a named dimension is partial, or when a stored
 * percent is missing or has more than two decimals (P50: never rounded, never derived from amounts).
 */
export function isAllocationsExactSplit(state: unknown, condition: unknown): boolean {
  const split = parseAllocationSplitCondition(condition);
  const bill = parseAllocationTargetsState(state);
  if (split === null || bill === null || !bill.split) return false;
  const shares = bill.split;
  for (const dimension of ALLOCATION_TARGET_DIMENSIONS) {
    const entries = split[dimension];
    if (
      entries !== undefined &&
      !holdsExactSplit(shares[dimension], entries, bill.partialDimensions.includes(dimension))
    ) {
      return false;
    }
  }
  for (const [segmentId, entries] of Object.entries(split.segments ?? {})) {
    if (
      !holdsExactSplit(
        shares.segments[segmentId],
        entries,
        bill.partialSegments.includes(segmentId),
      )
    ) {
      return false;
    }
  }
  return true;
}

/**
 * The `exact_split` value that a bill's own state would satisfy, for a rule made from that bill
 * (ALC-4565 prefill and its self-check read this one place), or `null` when the bill offers no
 * exact split: no bill-level SIMPLE split (`split` null or absent), a stored share whose percent is
 * `null` (missing, or more than two decimals, as "Dividir igualmente" 33.333 or a split typed as
 * amounts stores; P66, fail closed), a partial dimension, or percents that do not sum to 100.00.
 * Every dimension and segment the split uses is named; a dimension it does not use is left out.
 * A non-null result always saves (no `allocationTargetsConditionProblem`) and always matches the
 * state it came from.
 */
export function exactSplitValueFromState(state: unknown): AllocationSplit | null {
  const bill = parseAllocationTargetsState(state);
  if (bill === null || !bill.split) return null;
  const candidate: Record<string, unknown> = {};
  for (const dimension of ALLOCATION_TARGET_DIMENSIONS) {
    const shares = bill.split[dimension];
    if (shares.length === 0) continue;
    if (bill.partialDimensions.includes(dimension)) return null;
    if (shares.some((share) => share.percent === null)) return null;
    candidate[dimension] = shares.map(({ id, percent }) => ({ id, percent }));
  }
  const segments: Record<string, unknown> = {};
  for (const [segmentId, shares] of Object.entries(bill.split.segments)) {
    if (shares.length === 0) continue;
    if (bill.partialSegments.includes(segmentId)) return null;
    if (shares.some((share) => share.percent === null)) return null;
    segments[segmentId] = shares.map(({ id, percent }) => ({ id, percent }));
  }
  if (Object.keys(segments).length > 0) candidate.segments = segments;
  return parseAllocationSplitCondition(candidate);
}

/** The one entry point the evaluator uses for every operator of the fact. Unknown: false. */
export function evaluateAllocationTargets(
  operator: unknown,
  state: unknown,
  condition: unknown,
): boolean {
  switch (operator) {
    case 'all_within':
      return isAllocationsWithinTargets(state, condition);
    case 'same_targets':
      return isAllocationsSameTargets(state, condition);
    case 'exact_split':
      return isAllocationsExactSplit(state, condition);
    case 'any_within':
      return isAllocationsAnyWithin(state, condition);
    default:
      return false;
  }
}
